import { useCallback, useState, useSyncExternalStore, type ReactNode } from "react";
import { drawCharacterPreview } from "../pixel/characterSprites";
import { LANDING_LOOKS } from "./landingLooks";
import { LandingPlayground, type PlaygroundStatus } from "./LandingPlayground";

const reducedMotion = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
const coarsePointer = () => typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;

function caption(status: PlaygroundStatus, touch: boolean): string {
  switch (status.kind) {
    case "talk": return `You and ${status.name} can hear each other. Walk away and the call ends.`;
    case "muffled": return "Linh and Bảo are in a meeting. Walk in to listen.";
    case "meeting": return "You're in the meeting room. Only people inside can hear you.";
    case "door": return "Walk through the door to open your office.";
    case "entering": return "Opening your office…";
    case "walk": return touch ? "Tap the floor to walk. Get close to someone to talk." : "Walk with WASD or tap the floor. Get close to someone to talk.";
  }
}

/**
 * The landing page's walkable office, with its caption and character picker.
 * `cover` is the headline laid over the scene; it steps aside while the player walks behind it.
 */
export function LandingOffice({ onEnter, night, cover }: { onEnter: () => void; night: number; cover: ReactNode }) {
  const [runtime] = useState(() => new LandingPlayground({ onEnter, night, reducedMotion: reducedMotion() }));
  runtime.setNight(night);
  const status = useSyncExternalStore(runtime.subscribe, runtime.getStatus, runtime.getStatus);
  const [look, setLook] = useState(0);
  const [touch] = useState(coarsePointer);

  const mountCanvas = useCallback((canvas: HTMLCanvasElement | null) => {
    if (canvas) runtime.attach(canvas);
    else runtime.detach();
  }, [runtime]);

  const mountCover = useCallback((element: HTMLDivElement | null) => runtime.setCover(element), [runtime]);

  return (
    <>
      <div ref={mountCover} className="landing-cover" data-covered={status.covered || undefined}>{cover}</div>
      <div className="landing-office">
        <canvas
          ref={mountCanvas}
          className="landing-office-canvas"
          tabIndex={0}
          aria-label="A small demo office. Walk with WASD or the arrow keys, or tap the floor. Walk through the door on the right to sign in."
        />
        <div className="landing-office-hud">
          <p className="landing-office-caption" data-kind={status.kind} aria-live="polite">
            <span className="landing-office-dot" aria-hidden="true" />
            <span key={status.kind + (status.name ?? "")}>{caption(status, touch)}</span>
          </p>
          <div className="landing-looks" role="radiogroup" aria-label="Choose your character">
            {LANDING_LOOKS.slice(0, 6).map((appearance, index) => (
              <button
                key={index}
                type="button"
                role="radio"
                aria-checked={look === index}
                aria-label={`Character ${index + 1}`}
                className="landing-look"
                onClick={() => { setLook(index); runtime.setLook(index); }}
              >
                <canvas ref={(canvas) => { if (canvas) drawCharacterPreview(canvas, appearance, index, 2); }} aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
